import { createGlobalStyle, css, RuleSet } from "styled-components";

import {
  baseResetCSSWrapper,
  baseTextCSSWrapper,
  baseGridCSSWrapper,
  baseFormCSSWrapper,
  baseButtonCSSWrapper,
  baseSupplementalCSSWrapper,
} from "./layoutCSSWrapper";

// #############################################################################
// Theme Type Definition
// #############################################################################
interface ThemeBreakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
}

interface ThemeColors {
  menu: {
    base: { light: string; dark: string };
    baseAccent: { light: string; dark: string };
    text: { light: string; dark: string };
    textAccent: { light: string; dark: string };
    active: { light: string; dark: string };
    categoryIcon: { light: string; dark: string };
    categoryIconActive: { light: string; dark: string };
    mobileMenu: { light: string; dark: string };
    mobileMenuAccent: { light: string; dark: string };
    mobileMenuText: { light: string; dark: string };
    mobileMenuActive: { light: string; dark: string };
    modal: { light: string; dark: string };
    modalActiveItem: { light: string; dark: string };
  };
  white: string;
  black: string;
  black45: string;
  black50: string;
  black65: string;
  black85: string;
  shark: string;
  blackOrchid: string;
  primaryBlue: string;
  dodgerblue: string;
  athensGreyLight: string;
  athensGreyLight65: string;
  athensGreyDark: string;
}

export interface Theme {
  fontFamily: string;
  backgroundColor: string;
  Transition: (type?: string, duration?: number, delay?: number) => RuleSet<object>;
  TransitionDefault: RuleSet<object>;
  HeaderDefault: RuleSet<object>;
  TitleDefault: RuleSet<object>;
  DescriptionDefault: RuleSet<object>;
  LandingTitleDefault: RuleSet<object>;
  LandingDescriptionDefault: RuleSet<object>;
  GlideInDefault: RuleSet<object>;
  GenericButton: (color: string, height: string, padding: string, minWidth?: string) => RuleSet<object>;
  Breakingpoint: ThemeBreakpoints;
  break: ThemeBreakpoints;
  colors: ThemeColors;
  lineHeight: {
    header: number;
    subHeader: number;
  };
  font: {
    size10: string;
    size11: string;
    size12: string;
    size13: string;
    size14: string;
    size16: string;
  };
  fontWeight: {
    regular: number;
    medium: number;
    bold: number;
  };
  zIndex: {
    modal: number;
    modalUnderlay: number;
    header: number;
    searchOverLay: number;
    mobileMenu: number;
  };
}

// Module augmentation for styled-components - must be before theme definition
declare module "styled-components" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends Theme {}
}

// #############################################################################
// Global Style
// #############################################################################
export const POPOVER_COMPARE_CLASSNAME = "popover_compare";

export const GlobalStyle = createGlobalStyle`
  html,
  body {
    width: 100%;
    min-height: 100vh;

    &.lock-scroll {
      height: 100vh;
      max-height: 100vh;
    }
  }

  .lock-scroll {
    overflow: hidden !important;
    touch-action: none;
    -webkit-overflow-scrolling: touch;
  }

  ::selection {
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
  }

  ::-moz-selection {
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
  }
  html *,
  *:focus,
  *
  {
      outline: none !important;
  }
  button {
      overflow: visible;
  }
  button,
  input
  {
      margin: 0;
      padding: 0;
      width: auto;
  }
  button::-moz-focus-inner,
  input::-moz-focus-inner
  {
      border: 0;
      padding: 0;
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button,

  input[type="date"]::-webkit-inner-spin-button,
  input[type="date"]::-webkit-outer-spin-button,
  input[type="date"]::-webkit-calendar-picker-indicator
  {
      -webkit-appearance: none;
      margin: 0;
      display: none;
  }
  input[type="number"],
  input[type="date"]
  {
    -webkit-appearance: none;
      -moz-appearance: textfield;
  }

  a {
      color: $primary-link-color;
      [href^="tel:"] {
          color: $primary-text-color;
      }
  }
  input,
  textarea,
  select,
  button
  {
      -webkit-appearance: none;
      border-radius: 0;
  }

  b, strong {
      font-weight: 600;
  }

  .list-unstyled {
      margin: 0;
  }

  *, :before, :after {
      background-repeat: no-repeat;
      background-position: 50% 50%;
  }

  html, body {
    font-family: ${(props) => props.theme.fontFamily};

    -webkit-font-smoothing: antialiased;
    font-synthesis: none;
    font-variant-ligatures: no-common-ligatures;
  }


  html {
    background-color: ${(props) => props.theme.backgroundColor};
    height: 100%;
    min-height: 100%;
    position: relative;
  }

  body {
    font-family: ${(props) => props.theme.fontFamily};
    height: 100%;
    max-width: 100%;
    min-height: 100%;
    background-color: transparent;
    -moz-osx-font-smoothing: grayscale;
    &.mobile-menu-active {
      @media (max-width: 890px) {
        overflow: hidden;
      }
    }
  }

  video {
    -webkit-filter: brightness(102%);
  }

  a {
    cursor: pointer;
    text-decoration: none;
  }
  .offstage {
    position: absolute !important;
    overflow: hidden !important;
    clip: rect(0 0 0 0) !important;
    height: 1px !important;
    width: 1px !important;
    margin: -1px !important;
    padding: 0 !important;
    border: 0 !important;
  }

  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
    border-left: none;
    border-right: none;
    margin-top: 40px;
    margin-bottom: 35px;
  }

  #root {
    min-height: 100%;
  }

  .container-fluid {
    max-width: 1200px;
  }

  .app-t {
    display: table;
    table-layout: fixed;
    width: 100%;
    height: 100%;
  }
  .app-tr {
    display: table-row;
    width: 100%;
    height: 100%;
  }
  .app-tc {
    display: table-cell;
    width: 100%;
    height: 100%;
    vertical-align: middle;
  }

  .app-container {
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;
    overflow: visible;
    position: relative;
    max-width: 1390px;
  }

  .fade-in {
    ${(props) => props.theme.TransitionDefault}
    transform: translate3d(0px, -20px, 0px);
    transition-delay: 0s;
    opacity: 0;
		will-change: transform, opacity;
    &.idx-01 {
      transition-delay: 0.2s;
    }
    &.idx-02 {
      transition-delay: 0.4s;
    }
    &.idx-03 {
      transition-delay: 0.6s;
    }
    &.idx-04 {
      transition-delay: 0.8s;
    }
    &.idx-05 {
      transition-delay: 1s;
    }
    &.idx-06 {
      transition-delay: 1.2s;
    }
    &.idx-07 {
      transition-delay: 1.4s;
    }
    &.idx-08 {
      transition-delay: 1.6s;
    }

    .in-view & {
      transform: translate3d(0px, 0px, 0px);
      opacity: 1;
    }
  }

	.cross-fade-leave {
		opacity: 1;
		backface-visibility: hidden;
	}
	.cross-fade-leave.cross-fade-leave-active {
		backface-visibility: hidden;
		opacity: 0;
		transition: opacity 0.6s ease-in;
	}

	.cross-fade-enter {
		backface-visibility: hidden;
		opacity: 0;
	}
	.cross-fade-enter.cross-fade-enter-active {
		backface-visibility: hidden;
		opacity: 1;
		transition: opacity 0.6s ease-in;
	}

	.cross-fade-height {
		backface-visibility: hidden;
		transition: height 0.3s ease-in-out;

	}


  ${baseResetCSSWrapper};
  ${baseTextCSSWrapper};
  ${baseGridCSSWrapper};
  ${baseFormCSSWrapper};
  ${baseButtonCSSWrapper};
  ${baseSupplementalCSSWrapper};

`;

// End of global style

// #############################################################################
// CSS Helpers
// #############################################################################
const title_defaults = css`
  font-size: 32px;
  color: #4a4a4a;
  line-height: 1.4;
  text-align: center;
  -webkit-font-smoothing: antialiased;
  @media (max-width: ${(props) => props.theme.Breakingpoint.md}px) {
    font-size: 27px;
  }

  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 24px;
    padding-top: 5px;
    margin-bottom: 5px;
  }
`;

const TitleDefault = css`
  ${title_defaults}
`;

const description_defaults = css`
  font-size: 16px;
  color: #939393;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  text-align: center;
  line-height: ${22.4 / 16};
  @media (max-width: ${(props) => props.theme.Breakingpoint.md}px) {
    font-size: 18px;
  }
  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 16px;
  }
`;

const DescriptionDefault = css`
  ${description_defaults}
`;

const header_defaults = css`
  padding-left: 15px;
  padding-right: 15px;
`;

const HeaderDefault = css`
  ${header_defaults}
`;

const GlideInDefault = css`
  transition:
    opacity 0.2s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s,
    transform 0.2s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s;
  opacity: 0;
  .in-view & {
    opacity: 1;
  }
`;

const TransitionDefault = css`
  transition: all 0.9s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s;
`;

const Transition = (type?: string, duration?: number, delay?: number) => {
  return css`
    transition: ${type ? type : "all"} ${duration ? duration : 0.6}s
      cubic-bezier(0.26, 0.46, 0.45, 0.95) ${delay ? delay : 0}s;
  `;
};

const GenericButton = (color: string, height: string, padding: string, minWidth?: string) => {
  const colorScheme: Record<string, string> = {
    blue: "#007afe",
    black: "#1F1F1F",
    white: "#FFFFFF",
  };
  return css`
    ${(props) => props.theme.Transition("all", 0.3)}
    -webkit-font-smoothing: antialiased;
    font-weight: 700;
    font-size: 13px;
    color: ${colorScheme[color]};
    text-align: center;
    display: inline-block;
    vertical-align: middle;
    text-decoration: none;
    border: 2px solid ${colorScheme[color]};
    background-color: ${colorScheme[color]};
    color: #ffffff;
    border-radius: 6px;
    line-height: ${height};
    padding-left: ${padding};
    padding-right: ${padding};
    min-width: ${minWidth ? minWidth : "0px"};
    &:hover,
    &:active,
    &:focus,
    &.active {
      text-decoration: none;
      background-color: transparent;
      color: ${colorScheme[color]};
    }
  `;
};

const landing_title_defaults = css`
  font-size: 36px;
  color: #101010;
  line-height: 1;
  text-align: center;
  -webkit-font-smoothing: antialiased;
  font-weight: 800;
  margin-bottom: 10px;
  @media (max-width: ${(props) => props.theme.break.sm}px) {
    font-size: 32px;
  }
`;

const LandingTitleDefault = css`
  ${(props) => props.theme.GlideInDefault}
  ${landing_title_defaults}
`;

const landing_description_defaults = css`
  font-size: 22px;
  color: #9f9f9f;
  -webkit-font-smoothing: antialiased;
  text-align: center;
  line-height: ${22 / 20};
  @media (max-width: ${(props) => props.theme.break.sm}px) {
    font-size: 20px;
  }
`;

const LandingDescriptionDefault = css`
  ${(props) => props.theme.GlideInDefault}
  ${landing_description_defaults}
`;

// #############################################################################
// Theme Object
// #############################################################################
export const theme: Theme = {
  fontFamily: `"UI Sans", "Lato", sans-serif`,
  backgroundColor: "#ffffff",
  Transition,
  TransitionDefault,
  HeaderDefault,
  TitleDefault,
  DescriptionDefault,
  LandingTitleDefault,
  LandingDescriptionDefault,
  GlideInDefault,
  GenericButton,
  Breakingpoint: {
    xs: 480,
    sm: 767,
    md: 992,
    lg: 1200,
  },
  break: {
    xs: 480,
    sm: 767,
    md: 992,
    lg: 1200,
  },

  // Header rework added
  colors: {
    menu: {
      base: {
        light: "#FFFFFF",
        dark: "#000000",
      },
      baseAccent: {
        light: "#E5E5E5",
        dark: "#2D2D2D",
      },
      text: {
        light: "#393939",
        dark: "#FFFFFF",
      },
      textAccent: {
        light: "#8A8A8B",
        dark: "#8A8A8B",
      },
      active: {
        light: "#006FFF",
        dark: "#006FFF",
      },
      categoryIcon: {
        light: "#4D4D4D",
        dark: "#8E8E8E",
      },
      categoryIconActive: {
        light: "#1D1D1F",
        dark: "#FFFFFF",
      },
      mobileMenu: {
        light: "#1B1B1B",
        dark: "#1B1B1B",
      },
      mobileMenuAccent: {
        light: "#2D2D2D",
        dark: "#2D2D2D",
      },
      mobileMenuText: {
        light: "#F6F6F8",
        dark: "#F6F6F8",
      },
      mobileMenuActive: {
        light: "#121212",
        dark: "#121212",
      },
      modal: {
        light: "#FFFFFF",
        dark: "#FFFFFF",
      },
      modalActiveItem: {
        light: "#F5F5F5",
        dark: "#F5F5F5",
      },
    },
    white: "#FFFFFF",
    black: "#000000",
    black45: "rgba(0, 0, 0, 0.45)",
    black50: "rgba(0, 0, 0, 0.5)",
    black65: "rgba(0, 0, 0, 0.65)",
    black85: "rgba(0, 0, 0, 0.85)",
    shark: "#1D1D1F",
    blackOrchid: "#525461",
    primaryBlue: "#006FFF",
    dodgerblue: "#338CFF",
    athensGreyLight: "#F6F6F8",
    athensGreyLight65: "#F6F6F8A6",
    athensGreyDark: "#EDEDF0",
  },
  lineHeight: {
    header: 1.4,
    subHeader: 1.5,
  },
  font: {
    size10: "10px",
    size11: "11px",
    size12: "12px",
    size13: "13px",
    size14: "14px",
    size16: "16px",
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
  zIndex: {
    modal: 10000,
    modalUnderlay: 9995,
    header: 9000,
    searchOverLay: 8995,
    mobileMenu: 8990,
  },
};

// #############################################################################
// Constants
// #############################################################################
export const DEFAULT_MOTIF = "light";

const CUSTOM_WIDTH = {
  TOP_NAV_MOBILE: 991,
};

export const CONTAINER_WIDTH = {
  MOBILE: 460,
  TABLET: 768,
  DESKTOP: 1096,
};

export const CONTAINER_PADDING = {
  MOBILE: 16,
  TABLET: 32,
  DESKTOP: 32,
};

export const BREAKPOINTS = {
  SMALL_MOBILE_AND_DOWN: `(max-width: 376px)`,
  MOBILE_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.MOBILE + CONTAINER_PADDING.MOBILE * 2
  }px)`,
  TABLET_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.TABLET + CONTAINER_PADDING.TABLET * 2
  }px)`,
  DESKTOP_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.DESKTOP + CONTAINER_PADDING.DESKTOP * 2
  }px)`,
  TOP_NAV_MOBILE_AND_DOWN: `(max-width: ${CUSTOM_WIDTH.TOP_NAV_MOBILE}px)`,
  TOP_NAV_MOBILE_UP: `(min-width: ${CUSTOM_WIDTH.TOP_NAV_MOBILE + 1}px)`,
};
